import { apiInternals, send } from './client';

/**
 * `request()` is not itself exported from `client.ts` — see `cases.ts`'s own
 * docblock for why `apiInternals.request` is the one non-`send()` path a
 * production module outside `client.ts` may use: reading `meta` as well as
 * `data`.
 */
const { request } = apiInternals;

/**
 * The requests queue (M-09, Task 6) — `GET /case-requests`,
 * `GET /case-requests/{request}`, `POST /case-requests/{request}/complete`,
 * `POST /case-requests/{request}/resend`, `POST /case-requests/{request}/
 * calls`. Every shape here is verified field-for-field against
 * `App\Http\Resources\Requests\CaseRequestResource`/`CaseCallResource` and
 * `App\Support\OpenApi\ApiSpecification::caseRequestProperties()` in the API
 * repository, not inferred from the task brief's prose.
 *
 * ## `list` is built on `request`, never `send` (spec §4, `cases.ts`'s own rule)
 *
 * `GET /case-requests` is cursor-paginated exactly like `GET /cases` —
 * `{ data, meta: { next_cursor, per_page, total } }`. `send()` resolves to
 * `request()`'s `.data` alone and throws `meta` away; a `list` written as
 * `send<CaseRequestPage>(...)` would compile (the cast inside `request()` is
 * unchecked) but resolve to a bare `CaseRequestRow[]` with no `.meta` on it
 * at all, and the "next page" control would have nothing to read.
 *
 * ## `next_action` is read, never re-derived (Global Constraint, spec §4)
 *
 * `App\Support\Requests\NextAction::for()` is the single implementation of
 * the state machine — the API's own resource docblock says so in capitals.
 * This module carries `next_action` through unchanged; nothing here (or in
 * `RequestList.tsx`) computes its own copy of the waiting rule from
 * `resend`/`calls`/timestamps. Doing so is precisely the legacy defect
 * (`nonuploadedrequests.php` deciding what to show, `call.php` deciding what
 * to accept, and the two disagreeing) this milestone exists to fix.
 *
 * ## `kind`/`mobile`/`asked_at`/`asked_by_id` are each nullable
 *
 * `CaseRequestResource`'s own docblock: all four are read off the request's
 * first message, which is `null` only if a request somehow carries no
 * message at all — `SmsDispatcher`'s own transaction does not allow that in
 * practice, but the resource still emits `null` rather than omitting the
 * key, and this module types it that way rather than assuming presence.
 *
 * ## `calls[].action` is nullable (unlike `CaseCallResource`'s own `action`)
 *
 * `caseRequestProperties()`'s own comment: nullable "for a migrated row this
 * milestone does not itself produce" (Task 8's mapping runs after this
 * task). `CaseCallRow.action` (the standalone `POST .../calls` response) is
 * NOT nullable — a call this session just logged always carries the action
 * it was logged with.
 */

/** The three queues `GET /case-requests` filters over (spec D-4). Defaults to `open` server-side. */
/**
 * The three queues the requests SCREEN offers. They are disjoint by design —
 * every request is in exactly one — which is what makes them a work list.
 */
export type CaseRequestQueue = 'open' | 'awaiting' | 'complete';

/**
 * What `GET /case-requests` accepts, which is the three plus `all`.
 *
 * `all` is deliberately NOT a member of `CaseRequestQueue`: it is not a queue,
 * it is the absence of one, and the segmented control must not offer it.
 * The case-detail panel is its only caller — see `CaseRequests.tsx` for why
 * taking the `open` default there made that panel permanently empty.
 */
export type CaseRequestQueueFilter = CaseRequestQueue | 'all';

/** Every status the wire can answer — `caseRequestProperties()`'s own enum. `in_progress`/`cancelled` are reserved, not used by M-09 (spec D-4). */
export type CaseRequestStatus = 'open' | 'in_progress' | 'complete' | 'cancelled';

/** One of the nine `request.*` follow-up actions (spec D-7, D-8). */
export type CaseCallAction = 'reached' | 'no_reply' | 'unreachable';

/** One logged call, embedded in `CaseRequestRow.calls`. */
export interface CaseRequestCallRow {
  sequence: 1 | 2;
  /** Nullable — see this module's own docblock. */
  action: CaseCallAction | null;
  notes: string | null;
  called_at: string | null;
  called_by: number | null;
}

/** `CaseRequestResource`'s `case` key — `uuid` + `case_number`, never the integer foreign key. */
export interface CaseRequestCaseRef {
  uuid: string;
  case_number: string;
}

/**
 * One request, exactly as `GET /case-requests` and `GET /case-requests/
 * {request}` both render it — the SAME shape for both
 * (`CaseRequestResource`'s own class docblock).
 */
export interface CaseRequestRow {
  uuid: string;
  case: CaseRequestCaseRef | null;
  mobile: string | null;
  kind: string | null;
  status: CaseRequestStatus;
  asked_at: string | null;
  asked_by_id: number | null;
  uploaded_at: string | null;
  resend: {
    exists: boolean;
    /** The resend's own `queued_at`. `null` when `exists` is `false`. */
    at: string | null;
  };
  calls: readonly CaseRequestCallRow[];
  /**
   * THE SINGLE SOURCE OF WHICH BUTTON TO OFFER. Never re-derived client-side
   * — see this module's own docblock.
   */
  next_action: 'resend' | 'first_call' | 'second_call' | 'none';
}

/** One logged call, as the standalone `POST /case-requests/{request}/calls` response renders it — `CaseCallResource`'s own shape. */
export interface CaseCallRow {
  uuid: string;
  sequence: 1 | 2;
  action: CaseCallAction;
  notes: string | null;
  called_at: string | null;
  called_by: number | null;
}

/**
 * Every filter `GET /case-requests` accepts, `CaseRequestIndexRequest`'s own
 * rules. All optional. `case_uuid` DOES NOT EXIST server-side — Task 7's own
 * brief asked for it and the API repository carries no such filter
 * (`CaseRequestIndexRequest`/`CaseRequestController::index()`, read
 * directly, name only `case_number`); `CaseRequests.tsx` filters by
 * `case_number` instead, which the case detail screen already holds and
 * which is unique per case, exactly as `case_uuid` would have been.
 */
export interface CaseRequestQuery {
  queue?: CaseRequestQueueFilter;
  case_number?: string;
  mobile?: string;
  mine?: boolean;
  cursor?: string;
  per_page?: number;
}

/** One page of the queue — cursor-paginated, exactly `CasePage`/`CodePage`'s shape. */
export interface CaseRequestPage {
  data: readonly CaseRequestRow[];
  meta: {
    next_cursor: string | null;
    per_page: number;
    /** How many requests match the selected queue and filters, not how many are on this page. */
    total: number;
  };
}

/** `POST /case-requests/{request}/calls`'s body — `StoreCaseCallRequest`'s own two fields. NO `sequence` — the server assigns it (spec D-7). */
export interface LogCallInput {
  action: CaseCallAction;
  notes?: string | null;
}

/**
 * `?key=value&...`, or `''` when every value is absent. `undefined` is
 * dropped rather than becoming the literal string `'undefined'` in the URL —
 * the identical helper `cases.ts`/`codes.ts`/`client.ts` each redeclare
 * locally for the same reason (none is exported).
 */
function queryString(params: object): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params) as [string, string | number | boolean | undefined][]) {
    if (value === undefined) continue;

    /*
     * A BOOLEAN GOES OVER THE WIRE AS `1`/`0`, NEVER `true`/`false`.
     *
     * `String(true)` is `'true'`, and Laravel's `boolean` validation rule
     * accepts only `true`, `false`, `1`, `0`, `'1'` and `'0'` — the STRING
     * `'true'` is rejected. So `mine=true` was answered 422 by
     * `CaseRequestIndexRequest` before the controller's own coercion ever ran,
     * and ticking "Show mine" put the whole table into its error state.
     * Found by reading the server's rule, not by any test here: every test in
     * this module supplies its own fixture and never crosses the wire.
     */
    search.set(key, typeof value === 'boolean' ? (value ? '1' : '0') : String(value));
  }
  const built = search.toString();
  return built === '' ? '' : `?${built}`;
}

export const requestsApi = {
  /**
   * DELIBERATELY NOT `send<CaseRequestPage>()` — see this module's own
   * docblock for why that would compile and still lose `meta.next_cursor`.
   */
  list: async (query: CaseRequestQuery = {}): Promise<CaseRequestPage> => {
    const { data, meta } = await request<readonly CaseRequestRow[]>(`/case-requests${queryString(query)}`);
    return { data, meta: meta as CaseRequestPage['meta'] };
  },

  get: (uuid: string) => send<CaseRequestRow>(`/case-requests/${uuid}`),

  /** `409` when the request is already complete — distinguishable from a `422` by `error.status` (`cases.ts`'s own `close()` split). */
  complete: (uuid: string) => send<CaseRequestRow>(`/case-requests/${uuid}/complete`, { method: 'POST' }),

  /**
   * `409` when a resend already exists (spec D-6).
   *
   * Answers the REQUEST — the same shape `complete` does. It answered the
   * queued `SmsMessage` until review: the caller uses this response to replace
   * the row it acted on, matching on `uuid`, and a message's uuid matches no
   * row. The button stayed, the next click drew a 409, and the resend looked
   * like it had done nothing. The server changed rather than this client
   * papering over it, so the two row-mutating actions on this resource cannot
   * be told apart by their return type.
   */
  resend: (uuid: string) => send<CaseRequestRow>(`/case-requests/${uuid}/resend`, { method: 'POST' }),

  /**
   * `422` naming the reason when the computed sequence is not actually due
   * (`StoreCaseCallRequest`'s own docblock) — the server's answer to the
   * SAME `next_action` question this module's `list`/`get` already carry,
   * never a second rule this client enforces first.
   */
  logCall: (uuid: string, input: LogCallInput) =>
    send<CaseCallRow>(`/case-requests/${uuid}/calls`, { method: 'POST', body: input }),
};
